import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles, Mail, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const HANDOFF_MARKER = "[HANDOFF]";

const cleanText = (t) => t.replace(HANDOFF_MARKER, "").replace(/\[[A-Z]*$/, "").trim();

const QUICK_QUESTIONS = [
  "Πώς κλείνω συνεδρία;",
  "Πώς διαχειρίζομαι το άγχος;",
  "Κάνετε online συνεδρίες;",
];

const WELCOME = {
  role: "assistant",
  content:
    "Γεια σου! Είμαι ο ψηφιακός βοηθός της Άννας. Μπορώ να απαντήσω σε ερωτήσεις για τις συνεδρίες και τις υπηρεσίες της, αλλά και σε γενικές ερωτήσεις ψυχολογίας. Αν χρειαστεί, μπορώ να στείλω το μήνυμά σου απευθείας στην Άννα. Πώς μπορώ να βοηθήσω;",
};

function HandoffForm({ onSent, onCancel }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      setError("Συμπλήρωσε όνομα και email.");
      return;
    }
    setSending(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/ai-chat/handoff`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          note: form.note.trim() || null,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "failed");
      }
      onSent();
    } catch (e) {
      setError("Κάτι πήγε στραβά. Έλεγξε το email σου και δοκίμασε ξανά.");
    }
    setSending(false);
  };

  const inputCls =
    "w-full rounded-xl bg-background border border-border px-3.5 py-2.5 font-body text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60";

  return (
    <div
      data-testid="chat-handoff-form"
      className="rounded-2xl border border-primary/30 bg-primary/[0.06] p-4 space-y-2.5"
    >
      <p className="font-body text-sm font-medium text-foreground flex items-center gap-2">
        <Mail size={14} strokeWidth={1.5} className="text-primary" />
        Στείλε το μήνυμά σου απευθείας στην Άννα
      </p>
      <p className="font-body text-xs text-muted-foreground leading-relaxed">
        Θα λάβει τη συνομιλία σου και θα σου απαντήσει προσωπικά το συντομότερο.
      </p>
      <input
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        placeholder="Ονοματεπώνυμο *"
        data-testid="chat-handoff-name"
        className={inputCls}
      />
      <input
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        placeholder="Email *"
        type="email"
        data-testid="chat-handoff-email"
        className={inputCls}
      />
      <input
        value={form.phone}
        onChange={(e) => setForm({ ...form, phone: e.target.value })}
        placeholder="Τηλέφωνο (προαιρετικό)"
        data-testid="chat-handoff-phone"
        className={inputCls}
      />
      <textarea
        value={form.note}
        onChange={(e) => setForm({ ...form, note: e.target.value })}
        placeholder="Θέλεις να προσθέσεις κάτι; (προαιρετικό)"
        rows={2}
        data-testid="chat-handoff-note"
        className={`${inputCls} resize-none`}
      />
      {error && (
        <p data-testid="chat-handoff-error" className="font-body text-xs text-destructive">{error}</p>
      )}
      <div className="flex gap-2 pt-1">
        <button
          onClick={submit}
          disabled={sending}
          data-testid="chat-handoff-send"
          className="flex-1 rounded-full bg-foreground text-background px-4 py-2.5 text-xs font-body font-medium disabled:opacity-50 transition-opacity"
        >
          {sending ? "Αποστολή…" : "Αποστολή στην Άννα"}
        </button>
        <button
          onClick={onCancel}
          data-testid="chat-handoff-cancel"
          className="rounded-full border border-border px-4 py-2.5 text-xs font-body text-foreground/70"
        >
          Άκυρο
        </button>
      </div>
    </div>
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showHandoff, setShowHandoff] = useState(false);
  const [handoffSent, setHandoffSent] = useState(false);
  const scrollRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open, showHandoff]);

  if (location.pathname === "/krisi-panikou") return null;

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    setLoading(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    let full = "";
    try {
      const res = await fetch(`${API}/api/ai-chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        full += decoder.decode(value, { stream: true });
        const display = full;
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: display };
          return copy;
        });
      }
      if (full.includes(HANDOFF_MARKER) && !handoffSent) {
        setShowHandoff(true);
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Συγγνώμη, κάτι πήγε στραβά. Δοκίμασε ξανά ή κάλεσε στο 697 8042 360.",
        };
        return copy;
      });
    }
    setLoading(false);
  };

  const onHandoffSent = () => {
    setShowHandoff(false);
    setHandoffSent(true);
    setMessages((m) => [
      ...m,
      {
        role: "assistant",
        content:
          "Το μήνυμά σου στάλθηκε στην Άννα μαζί με τη συνομιλία μας. Θα επικοινωνήσει προσωπικά μαζί σου το συντομότερο δυνατό. 💚",
      },
    ]);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            data-testid="chat-panel"
            className="fixed bottom-24 right-4 sm:right-6 z-[70] w-[calc(100%-2rem)] max-w-[400px] h-[540px] max-h-[calc(100vh-8rem)] bg-background border border-border rounded-3xl shadow-[0_24px_80px_rgb(42,54,46,0.25)] flex flex-col overflow-hidden"
          >
            <div className="px-5 py-4 bg-foreground text-background flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-background/15 flex items-center justify-center">
                  <Sparkles size={16} strokeWidth={1.5} />
                </div>
                <div>
                  <p className="font-heading text-lg leading-tight">Ψηφιακός Βοηθός</p>
                  <p className="text-[11px] text-background/70 font-body">psyloveyourself · AI</p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="chat-close"
                className="p-1.5 text-background/70 hover:text-background transition-colors"
                aria-label="Κλείσιμο"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={m.role === "user" ? "chat-user-message" : "chat-assistant-message"}
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 font-body text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-foreground text-background rounded-br-md"
                        : "bg-card border border-border/60 text-foreground rounded-bl-md"
                    }`}
                  >
                    {m.role === "assistant" ? cleanText(m.content) || (
                      <span className="inline-flex gap-1 items-center py-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-foreground/40 animate-bounce [animation-delay:0ms]" />
                        <span className="w-1.5 h-1.5 rounded-full bg-foreground/40 animate-bounce [animation-delay:150ms]" />
                        <span className="w-1.5 h-1.5 rounded-full bg-foreground/40 animate-bounce [animation-delay:300ms]" />
                      </span>
                    ) : m.content}
                  </div>
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {QUICK_QUESTIONS.map((q) => (
                    <button
                      key={q}
                      onClick={() => send(q)}
                      data-testid="chat-quick-question"
                      className="rounded-full border border-border bg-card px-3.5 py-2 text-xs font-body text-foreground/80 hover:bg-primary/10 hover:border-primary/40 transition-all"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
              {showHandoff && !handoffSent && (
                <HandoffForm onSent={onHandoffSent} onCancel={() => setShowHandoff(false)} />
              )}
            </div>

            <div className="px-4 pb-4 pt-2 shrink-0 border-t border-border/50">
              {!showHandoff && !handoffSent && (
                <button
                  onClick={() => setShowHandoff(true)}
                  data-testid="chat-handoff-open"
                  className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-body text-primary hover:text-foreground transition-colors"
                >
                  <Mail size={11} strokeWidth={1.5} />
                  Στείλε μήνυμα απευθείας στην Άννα
                </button>
              )}
              {handoffSent && (
                <p
                  data-testid="chat-handoff-success"
                  className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-body text-primary"
                >
                  <Check size={11} strokeWidth={2} />
                  Το μήνυμά σου στάλθηκε στην Άννα
                </p>
              )}
              <div className="flex items-center gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Γράψε την ερώτησή σου…"
                  data-testid="chat-input"
                  className="flex-1 rounded-full bg-card border border-border px-4 py-2.5 font-body text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/60"
                />
                <button
                  onClick={() => send()}
                  disabled={loading || !input.trim()}
                  data-testid="chat-send"
                  className="w-10 h-10 shrink-0 rounded-full bg-foreground text-background flex items-center justify-center disabled:opacity-40 transition-opacity"
                  aria-label="Αποστολή"
                >
                  <Send size={15} strokeWidth={1.5} />
                </button>
              </div>
              <p className="mt-2 text-[10px] leading-snug text-muted-foreground font-body text-center">
                AI βοηθός — δεν παρέχει διάγνωση ή θεραπεία. Σε επείγον: 166 / 1018.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        onClick={() => setOpen(!open)}
        data-testid="chat-toggle"
        className="fixed bottom-20 md:bottom-6 right-4 sm:right-6 z-[65] w-14 h-14 rounded-full bg-foreground text-background shadow-[0_12px_40px_rgb(42,54,46,0.35)] flex items-center justify-center hover:scale-105 transition-transform"
        aria-label="Άνοιγμα βοηθού συνομιλίας"
      >
        {open ? <X size={22} strokeWidth={1.5} /> : <MessageCircle size={22} strokeWidth={1.5} />}
      </motion.button>
    </>
  );
}
